import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import config from "../config";
import routing from "../routing";
import SessionExpiredOverlay from "../components/dialog/SessionExpiredOverlay";
import LogoutConfirmationDialog from "../components/dialog/LogoutConfirmationDialog";
import { hasUnsavedChanges, setUnsavedChanges } from "../utilities/unsavedChanges";
import { clearUserCache } from "../utilities/queryClient";
import { Logout as AuthLogout } from "../db_access/logout";

const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const storedUser = localStorage.getItem("user");
    return storedUser ? JSON.parse(storedUser) : null;
  });

  const [sessionExpired, setSessionExpired] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [pendingLogoutAction, setPendingLogoutAction] = useState(null);
  const hasFlag = useCallback(
    (key) => {
      if (!user) return false;
      const value = user[key];
      // Handle both number 1, boolean true, and string "1"
      return value === 1 || value === true || value === "1";
    },
    [user]
  );
  const getFlag = useCallback(
    (key) => {
      if (!user) return null;
      return user[key];
    },
    [user]
  );
  const isAdmin = useMemo(() => hasFlag("is_admin"), [hasFlag]);
  const isCompanyAdmin = useMemo(() => hasFlag("is_company_admin"), [hasFlag]);
  const isImpersonating = useMemo(() => hasFlag("is_impersonating"), [hasFlag]);
  const impersonatorID = useMemo(() => getFlag("impersonator_id"), [getFlag]);

  // Master Admins get all modules enabled ONLY if not currently impersonating a limited user
  const shouldOverride = useMemo(() => isAdmin && !isImpersonating, [isAdmin, isImpersonating]);

  const isProductEnabled = useMemo(() => shouldOverride || hasFlag("is_product_enabled"), [shouldOverride, hasFlag]);
  const isProjectEnabled = useMemo(() => shouldOverride || hasFlag("is_project_enabled"), [shouldOverride, hasFlag]);
  const isBuildingEnabled = useMemo(() => shouldOverride || hasFlag("is_building_enabled"), [shouldOverride, hasFlag]);
  const isSectorEnabled = useMemo(() => isProductEnabled || isProjectEnabled || isBuildingEnabled, [isProductEnabled, isProjectEnabled, isBuildingEnabled]);
  const isOrganizationEnabled = useMemo(() => shouldOverride || hasFlag("is_organization_enabled"), [shouldOverride, hasFlag]);
  const isCollaborateInternalEnabled = useMemo(() => shouldOverride || hasFlag("is_collaborate_internal_enabled"), [shouldOverride, hasFlag]);
  const isCollaborateExternalEnabled = useMemo(() => shouldOverride || hasFlag("is_collaborate_external_enabled"), [shouldOverride, hasFlag]);
  const isEngagementEnabled = useMemo(() => isCollaborateInternalEnabled || isCollaborateExternalEnabled, [isCollaborateInternalEnabled, isCollaborateExternalEnabled]);
  const isDppEnabled = useMemo(() => shouldOverride || hasFlag("is_dpp_enabled"), [shouldOverride, hasFlag]);
  const isAIInsightEnabled = useMemo(() => shouldOverride || hasFlag("is_ai_insight_enabled"), [shouldOverride, hasFlag]);
  const isArivuChatEnabled = useMemo(() => shouldOverride || hasFlag("is_arivu_chat_enabled"), [shouldOverride, hasFlag]);
  const isSupportAccessEnabled = useMemo(() => hasFlag("is_support_access_enabled"), [hasFlag]);
  const username = useMemo(() => getFlag("user_id"), [getFlag]);
  const companyID = useMemo(() => getFlag("company_id"), [getFlag]);
  const companyName = useMemo(() => getFlag("company_name"), [getFlag]);

  /* 
  // Test dashboard access: admin or specific usernames (dev, vrtta, superadmin)
  const canAccessTestDashboard = useMemo(() => {
    if (hasFlag("is_admin")) return true;
    const userId = getFlag("user_id")?.toLowerCase() || "";
    return ["dev", "vrtta", "superadmin"].includes(userId);
  }, [hasFlag, getFlag]);
  */

  const login = async (newUser) => {
    // Clear potentially stale cache before logging in new user
    await clearUserCache();

    // Stamp an expiry that mirrors the JWT access-token lifetime (1 hour).
    // Without this, localStorage would persist the session forever across
    // browser restarts, letting a user re-open the app days later and skip
    // the login screen entirely.
    const userData = {
      ...newUser,
      expires: Date.now() + 60 * 60 * 1000, // 1 hour in ms
    };
    localStorage.setItem("user", JSON.stringify(userData));
    setUser(userData);
  };

  const logout = useCallback(async () => {
    try {
      // 1. Tell backend to invalidate session/clear cookies
      await AuthLogout.clear_token();
    } catch (e) {
      
    }

    // 2. Wipe IndexedDB and React Query cache
    await clearUserCache();

    // 3. Clear local session, auth keys, and residual client cookies
    localStorage.removeItem("user");
    localStorage.removeItem("termsAccepted");
    localStorage.removeItem("username");
    sessionStorage.clear();
    try {
      document.cookie.split(";").forEach((c) => {
        document.cookie = c
          .replace(/^ +/, "")
          .replace(/=.*/, "=;expires=" + new Date(0).toUTCString() + ";path=/");
      });
    } catch (e) {}

    setUnsavedChanges(false);
    setUser(null);
   
    
    // 4. Force a page reload/redirect to completely reset all React memory contexts
    window.location.href = routing.login;
  }, [user]);

  const loginAsUser = useCallback(async (targetUserId, reason = "Support access") => {
    try {
      const response = await postWithToken(`${config.apiUrl}/user/impersonate`, { target_user_id: targetUserId, reason });
      if (response.ok) {
        const data = await response.json();
        // 1. Save new user data
        localStorage.setItem("user", JSON.stringify(data.user));

        // 2. Reload (Backend has already set the new cookies)
        window.location.reload();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || "Impersonation failed");
      }
    } catch (error) {
      
      throw error;
    }
  }, []);

  const stopImpersonating = useCallback(async () => {
    try {
      const response = await postWithToken(`${config.apiUrl}/user/stop_impersonate`, {});
      if (response.ok) {
        const data = await response.json();
        // 1. Save Admin data back
        localStorage.setItem("user", JSON.stringify(data.user));

        // 2. Reload (Backend has already restored Admin cookies)
        window.location.reload();
      } else {
        
        logout();
        window.location.reload();
      }
    } catch (error) {
      // Even if network fails, we should clear state to prevent being stuck
      logout();
      window.location.reload();
    }
  }, [logout]);

  /**
   * Safe logout function that checks for unsaved changes first
   */
  const attemptLogout = useCallback((customAction) => {
    const isDirty = hasUnsavedChanges();

    if (isDirty) {
      // Show confirmation if there's unsaved work
      setPendingLogoutAction(() => customAction || logout);
      setShowLogoutConfirm(true);
    } else {
      // Otherwise logout immediately
      if (customAction) {
        customAction();
      } else {
        logout();
      }
    }
  }, [logout]);

  const confirmLogout = () => {
    setShowLogoutConfirm(false);
    if (pendingLogoutAction) {
      pendingLogoutAction();
      setPendingLogoutAction(null);
    }
  };

  const cancelLogout = () => {
    setShowLogoutConfirm(false);
    setPendingLogoutAction(null);
  };

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        const now = Date.now();

        // If an expiry is present and has passed, clear the stale session.
        // This is the fix for: close browser → reopen link → land directly on
        // a protected page without re-authenticating.
        if (userData.expires && userData.expires < now) {
          console.warn('[Auth] ⚠️ Stored session has expired. Clearing and redirecting to login.');
          localStorage.removeItem('user');
          localStorage.removeItem('termsAccepted');
          setUser(null);
          return;
        }

        setUser(userData);
      } catch (e) {
        console.error('[Auth] ❌ Failed to parse stored user:', e);
        logout();
      }
    }
  }, []);
  // Note: `logout` is intentionally omitted from deps — it is stable (useCallback)
  // and including it would cause an infinite re-render loop on first mount.

  // Multi-Tab Sync: Detect logout in another tab
  useEffect(() => {
    const handleStorageChange = (event) => {
      // If the 'user' key is removed in another tab, it means a logout happened
      if (event.key === "user" && !event.newValue && user) {
        
        setSessionExpired(true);
        setUser(null);
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [user]);

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        isAdmin,
        isCompanyAdmin,
        isProductEnabled,
        isProjectEnabled,
        isBuildingEnabled,
        isDppEnabled,
        isSectorEnabled,
        isOrganizationEnabled,
        isEngagementEnabled,
        isAIInsightEnabled,
        isArivuChatEnabled,
        isSupportAccessEnabled,
        // canAccessTestDashboard,
        username,
        companyID,
        companyName,
        attemptLogout,
        setUnsavedChanges,
        isImpersonating,
        impersonatorID,
        loginAsUser,
        stopImpersonating
      }}>
      {children}
      {sessionExpired && <SessionExpiredOverlay />}
      <LogoutConfirmationDialog
        open={showLogoutConfirm}
        onConfirm={confirmLogout}
        onCancel={cancelLogout}
      />
    </AuthContext.Provider>
  );
};

// flask-jwt-extended's double-submit CSRF cookies are deliberately NOT
// HttpOnly, so this is readable JS -- that's what lets our own frontend (but
// not a cross-site attacker page) prove a request actually came from here.
function getCookie(name) {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

// Shared in-flight refresh promise: when several requests hit 401 around the
// same moment (e.g. a dashboard firing multiple parallel calls on one shared,
// now-expired token), they all await this single promise instead of each
// firing its own /refresh call and clobbering localStorage with a different
// winning token.
let refreshPromise = null;

async function performTokenRefresh() {
  const storedUser = localStorage.getItem("user");
  const refreshToken = storedUser ? JSON.parse(storedUser).refresh_token : null;

  const refreshCsrfToken = getCookie("csrf_refresh_token");
  const refreshRes = await fetch(config.refresh, {
    ...config.method.POST,
    headers: {
      ...config.header.json,
      ...(refreshToken ? { "Authorization": `Bearer ${refreshToken}` } : {}),
      ...(refreshCsrfToken ? { "X-CSRF-TOKEN": refreshCsrfToken } : {}),
    },
    ...config.credential.include
  });

  if (!refreshRes.ok) {
    throw new Error("Token refresh failed");
  }

  const refreshData = await refreshRes.json().catch(() => null);
  const newAccessToken = refreshData?.access_token;

  if (newAccessToken && storedUser) {
    const updatedUser = {
      ...JSON.parse(storedUser),
      access_token: newAccessToken,
      expires: Date.now() + 60 * 60 * 1000, // Slide 1-hour expiration window forward for active sessions
    };
    localStorage.setItem("user", JSON.stringify(updatedUser));
  }

  return newAccessToken;
}

function refreshAccessToken() {
  if (!refreshPromise) {
    refreshPromise = performTokenRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

export async function fetchWithToken(url, options = {}) {
  const storedUser = localStorage.getItem("user");
  const token = storedUser ? JSON.parse(storedUser).access_token : null;

  // Create a new headers object or use the existing one
  const headers = new Headers(options.headers || {});

  // Add the Authorization header if we have a token and it's not already set
  if (token && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  if (!headers.has("X-CSRF-TOKEN")) {
    const csrfToken = getCookie("csrf_access_token");
    if (csrfToken) headers.set("X-CSRF-TOKEN", csrfToken);
  }

  const response = await fetch(url, {
    ...options,
    headers,
    ...config.credential.include
  });

  if (response.status === 401) {
    

    try {
      const newAccessToken = await refreshAccessToken();
      

      // Retry original request with the refreshed token attached
      const retryHeaders = new Headers(options.headers || {});
      if (newAccessToken) {
        retryHeaders.set("Authorization", `Bearer ${newAccessToken}`);
      } else if (token) {
        retryHeaders.set("Authorization", `Bearer ${token}`);
      }
      // /refresh just minted a new csrf_access_token cookie -- read it fresh
      // rather than reusing whatever was attached to the original request.
      const freshCsrfToken = getCookie("csrf_access_token");
      if (freshCsrfToken) retryHeaders.set("X-CSRF-TOKEN", freshCsrfToken);

      return fetch(url, { ...options, headers: retryHeaders, ...config.credential.include });
    } catch (e) {
      
      // Clear local session before redirecting
      localStorage.removeItem("user");
      window.location.href = routing.login;
    }
  }

  return response;
}

const jsonWithNulls = (obj) => JSON.stringify(obj, (_k, v) => (v === undefined ? null : v));
export const postWithToken = (url, body = {}, options = {}) => {
  return fetchWithToken(url, {
    ...config.method.POST,
    headers: config.header.json,
    body: jsonWithNulls(body),
    ...options,
  });
};

const withParams = (url, params) => {
  const queryString = params && Object.keys(params).length > 0
    ? "?" + new URLSearchParams(params).toString()
    : "";
  return url + queryString;
};
export const getWithToken = (url, params = {}, options = {}) => {
  return fetchWithToken(withParams(url, params), { ...config.method.GET, ...options });
};
